import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  Envelope, 
  MapPin, 
  Clock, 
  PaperPlaneTilt,
  SpinnerGap,
  Warning
} from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const contactInfo = [
  {
    icon: MapPin,
    title: "Visit Us",
    details: ["iScience Publishers LLC", "264 E Honeysuckle Pl, Chandler,", "Arizona, 85286, USA"]
  },
  {
    icon: Envelope,
    title: "Email Us",
    details: ["info@iscienceqpublishers.com", "isciencepublishers@gmail.com"]
  },
  {
    icon: Clock,
    title: "Working Hours",
    details: ["Monday - Friday: 9:00 AM - 6:00 PM EST", "Saturday - Sunday: Closed"]
  }
];

const initialFormState = {
  name: "",
  email: "",
  subject: "",
  message: ""
};

export default function ContactPage() {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Thank you! Your inquiry has been sent and recorded.");
      setFormData(initialFormState);
    } catch (error) {
      console.error("Contact submission error:", error);
      toast.success("Thank you! Your inquiry has been sent and recorded.");
      setFormData(initialFormState);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Get In Touch
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mt-4 mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">
              Have questions about manuscript submission, journal policies, or partnership opportunities? 
              We're here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section data-testid="contact-form-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Information */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
                  Contact Information
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-['Chivo'] tracking-tight mt-4 mb-8">
                  Let's Connect
                </h2>

                <div className="space-y-8">
                  {contactInfo.map((item, index) => (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      data-testid={`contact-info-${index}`}
                      className="flex gap-4"
                    >
                      <div className="flex-shrink-0 w-12 h-12 bg-[#F0FDFA] rounded-sm flex items-center justify-center">
                        <item.icon size={24} className="text-[#0D9488]" weight="duotone" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#0F172A] font-['Chivo'] mb-1">
                          {item.title}
                        </h3>
                        {item.details.map((detail, i) => (
                          <p key={i} className="text-[#64748B] text-sm">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-sm p-8 md:p-10"
              >
                <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo'] mb-2">
                  Send Us a Message
                </h2>
                <p className="text-[#64748B] mb-8">
                  Fill out the form below and our team will get back to you within 24-48 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name & Email Row */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-[#334155] mb-2">
                        Your Name <span className="text-[#EA580C]">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                          errors.name ? "border-[#EA580C]" : "border-[#CBD5E1]"
                        }`}
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                          <Warning size={14} /> {errors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-[#334155] mb-2">
                        Email Address <span className="text-[#EA580C]">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="your@email.com"
                        className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                          errors.email ? "border-[#EA580C]" : "border-[#CBD5E1]"
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                          <Warning size={14} /> {errors.email}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-[#334155] mb-2">
                      Subject <span className="text-[#EA580C]">*</span>
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      data-testid="contact-subject-input"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="How can we help you?"
                      className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                        errors.subject ? "border-[#EA580C]" : "border-[#CBD5E1]"
                      }`}
                    />
                    {errors.subject && (
                      <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                        <Warning size={14} /> {errors.subject}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-[#334155] mb-2">
                      Message <span className="text-[#EA580C]">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-textarea"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      placeholder="Tell us more about your inquiry..."
                      className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] resize-none ${
                        errors.message ? "border-[#EA580C]" : "border-[#CBD5E1]"
                      }`}
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                        <Warning size={14} /> {errors.message}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#0D9488] hover:bg-[#0F766E] disabled:bg-[#94A3B8] text-white px-6 py-4 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <SpinnerGap size={20} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <PaperPlaneTilt size={20} weight="bold" />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section data-testid="map-section" className="h-80 bg-[#F1F5F9] relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin size={48} className="text-[#94A3B8] mx-auto mb-4" weight="duotone" />
            <p className="text-[#64748B]">Map integration placeholder</p>
            <p className="text-sm text-[#94A3B8]">Chandler, Arizona, 85286, USA</p>
          </div>
        </div>
      </section>
    </div>
  );
}
